import apiClient from './api';
import { ProjectReport, Finding } from '../types/api.types';
import { findingService } from './findingService';

/**
 * Request interface for report generation
 */
export interface ReportGenerationRequest {
  project_id: string;
  title: string;
  include_artifacts: boolean;
  include_findings: boolean;
  query?: string;
  max_findings?: number;
  max_artifacts?: number;
}

/**
 * Options for report generation
 */
export interface ReportGenerationOptions {
  title?: string;
  includePentestScans?: boolean;  // Include pentest scans (burp, cisco, ffuf, nikto, etc.)
  includeCtemScans?: boolean;      // Include CTEM/Falcon scans
  includeArtifacts?: boolean;      // Include artifacts in the report
  includeFindings?: boolean;       // Include findings in the report
  query?: string;
}

/**
 * Service for managing project reports
 */
export class ReportService {
  private pentestReportsUrl = '/ctem/ui/reports';  
  /**
   * Get URL to directly access a report's content
   */
  getReportFileUrl(reportId: string, reportTitle?: string): string {
    return `${apiClient.getBaseUrl()}${this.pentestReportsUrl}/${reportId}/content`;
  }

  /**
   * Get URL to download a report as Word document (.docx)
   */
  getReportDocxUrl(reportId: string): string {
    return `${apiClient.getBaseUrl()}${this.pentestReportsUrl}/${reportId}/download/docx`;
  }

  /**
   * Download a report as Word document (.docx)
   * 
   * @param reportId Report ID to download
   * @returns Promise that resolves when download starts
   */
  async downloadReportAsDocx(reportId: string): Promise<void> {
    try {
      console.log(`📥 Downloading report ${reportId} as Word document`);
      
      const url = this.getReportDocxUrl(reportId);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || 'dev-key'}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to download Word document: ${response.statusText}`);
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `report-${reportId}.docx`;

      // Download the file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      console.log(`✅ Word document downloaded: ${filename}`);
    } catch (error) {
      console.error('Error downloading Word document:', error);
      throw error;
    }
  }

  /**
   * Generate a new report for a project using the dedicated report API
   * 
   * @param projectId Project ID to generate report for
   * @param options Report generation options
   * @returns A promise with the generation status
   */
  async generateReport(
    projectId: string,
    options: ReportGenerationOptions = {}
  ): Promise<{ report_id: string; status: string; message: string }> {
    try {
      const {
        title = "Penetration Test Report",
        includePentestScans = true,
        includeCtemScans = false,
        includeArtifacts = true,
        includeFindings = true,
        query
      } = options;
      
      // When both scan types are requested, use pentest endpoint with scan_type=all
      if (includePentestScans && includeCtemScans) {
        console.log(`📊 Generating comprehensive report with all scans (pentest + CTEM/Falcon)`);
        
        const requestData: ReportGenerationRequest = {
          project_id: projectId,
          title,
          include_artifacts: includeArtifacts,
          include_findings: includeFindings
        };
        
        if (query) {
          requestData.query = query;
        }
        
        console.log('📤 POST /ctem/ui/reports/generate?scan_type=all');
        console.log('  Request body:', requestData);
        
        // TODO BACKEND: Add scan_type=all parameter support (currently works without it as default)
        const response = await apiClient.post<{ report_id: string; status: string; message: string }>(
          `${this.pentestReportsUrl}/generate?scan_type=all`,
          requestData
        );
        
        console.log('  ← Response:', response);
        return response;
      } 
      // Only pentest scans (burp, cisco, ffuf, nikto, etc. - excludes falcon)
      else if (includePentestScans && !includeCtemScans) {
        console.log(`📊 Generating pentest-only report (excludes Falcon)`);
        
        const requestData: ReportGenerationRequest = {
          project_id: projectId,
          title,
          include_artifacts: includeArtifacts,
          include_findings: includeFindings
        };
        
        if (query) {
          requestData.query = query;
        }
        
        console.log('📤 POST /ctem/ui/reports/generate?scan_type=pentest');
        console.log('  Request body:', requestData);
        
        // TODO BACKEND: Implement scan_type=pentest parameter to filter out Falcon scans
        const response = await apiClient.post<{ report_id: string; status: string; message: string }>(
          `${this.pentestReportsUrl}/generate?scan_type=pentest`,
          requestData
        );
        
        console.log('  ← Response:', response);
        return response;
      } 
      // Only CTEM/Falcon scans
      else if (!includePentestScans && includeCtemScans) {
        console.log(`📊 Generating CTEM/Falcon-only report`);
        
        const requestData: ReportGenerationRequest = {
          project_id: projectId,
          title,
          include_artifacts: includeArtifacts,
          include_findings: includeFindings
        };
        
        if (query) {
          requestData.query = query;
        }
        
        console.log('📤 POST /ctem/ui/reports/generate?scan_type=ctem');
        console.log('  Request body:', requestData);
        
        // TODO BACKEND: Implement scan_type=ctem parameter to return ONLY Falcon/CTEM scans
        try {
          const response = await apiClient.post<{ report_id: string; status: string; message: string }>(
            `${this.pentestReportsUrl}/generate?scan_type=ctem`,
            requestData
          );
          console.log('  ← Response:', response);
          return response;
        } catch (error: any) {
          if (error.response?.status === 404 || error.response?.status === 400) {
            console.error('❌ scan_type=ctem parameter not yet implemented');
            const ctemError = new Error('CTEM-only reports are not yet available. The backend team needs to implement scan_type=ctem parameter support.');
            (ctemError as any).isCtemUnavailable = true;
            throw ctemError;
          } else {
            throw error;
          }
        }
      }
      // Neither scan type selected
      else {
        console.log(`📊 No scans selected for report generation`);
        throw new Error('Please select at least one scan type (Pentest or CTEM) for report generation');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  }

  /**
   * Get all reports for a project
   * 
   * @param projectId Project ID to get reports for
   * @returns A promise with an array of reports
   */
  async getReports(projectId: string): Promise<ProjectReport[]> {
    try {
      console.log('📥 getReports() called');
      console.log('  → GET /ctem/ui/reports?project_id=' + projectId + '&scan_type=all');
      // TODO BACKEND: Add scan_type=all parameter support (currently works without it as default)
      const allReports = await apiClient.get<ProjectReport[]>(
        `${this.pentestReportsUrl}?project_id=${projectId}&scan_type=all`
      );
      console.log('  ← Received', allReports.length, 'reports (all scans)');
      return allReports;
    } catch (error) {
      console.error('Error getting reports:', error);
      throw error;
    }
  }

  /**
   * Get pentest-only reports for a project
   * 
   * @param projectId Project ID to get reports for
   * @returns A promise with an array of pentest reports
   */
  async getPentestReports(projectId: string): Promise<ProjectReport[]> {
    try {
      console.log('📥 getPentestReports() called');
      console.log('  → GET /ctem/ui/reports?project_id=' + projectId + '&scan_type=pentest');
      // TODO BACKEND: Add scan_type=pentest parameter support
      const pentestReports = await apiClient.get<ProjectReport[]>(
        `${this.pentestReportsUrl}?project_id=${projectId}&scan_type=pentest`
      );
      console.log('  ← Received', pentestReports.length, 'pentest-only reports');
      return pentestReports;
    } catch (error) {
      console.error('Error getting pentest reports:', error);
      throw error;
    }
  }

  /**
   * Get CTEM-only reports for a project
   * 
   * @param projectId Project ID to get reports for
   * @returns A promise with an array of CTEM reports
   */
  async getCtemReports(projectId: string): Promise<ProjectReport[]> {
    try {
      console.log('📥 getCtemReports() called');
      console.log('  → GET /ctem/ui/reports?project_id=' + projectId + '&scan_type=ctem');
      // TODO BACKEND: Add scan_type=ctem parameter support
      const ctemReports = await apiClient.get<ProjectReport[]>(
        `${this.pentestReportsUrl}?project_id=${projectId}&scan_type=ctem`
      );
      console.log('  ← Received', ctemReports.length, 'CTEM-only reports');
      return ctemReports;
    } catch (error) {
      console.error('Error getting CTEM reports:', error);
      throw error;
    }
  }

  /**
   * Get a specific report by ID
   * 
   * @param reportId Report ID to get
   * @returns A promise with the report data
   */
  async getReport(reportId: string): Promise<ProjectReport> {
    try {
      return await apiClient.get<ProjectReport>(`${this.pentestReportsUrl}/${reportId}`);
    } catch (error) {
      console.error('Error getting report:', error);
      throw error;
    }
  }

  /**
   * Get the latest report for a project
   * 
   * @param projectId Project ID to get report for
   * @returns A promise with the report data or null if no reports exist
   */
  async getLatestReport(projectId: string): Promise<ProjectReport | null> {
    try {
      const reports = await this.getReports(projectId);
      
      // Sort by created_at descending and get the first one
      const sortedReports = [...reports].sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });
      
      return sortedReports.length > 0 ? sortedReports[0] : null;
    } catch (error) {
      console.error('Error getting latest report:', error);
      return null;
    }
  }

  /**
   * Check if a project has a completed report
   * 
   * @param projectId Project ID to check
   * @returns A promise that resolves to true if the project has at least one completed report
   */
  async hasCompletedReport(projectId: string): Promise<boolean> {
    try {
      const reports = await this.getReports(projectId);
      return reports.some(report => report.status === 'completed');
    } catch (error) {
      console.error('Error checking for reports:', error);
      return false;
    }
  }
  
  /**
   * Delete a report by ID
   * 
   * @param reportId Report ID to delete
   * @returns A promise that resolves to a success message
   */
  async deleteReport(reportId: string): Promise<{ message: string }> {
    try {
      return await apiClient.delete<{ message: string }>(`${this.pentestReportsUrl}/${reportId}`);
    } catch (error) {
      console.error('Error deleting report:', error);
      throw error;
    }
  }

  /**
   * Get the status of a report generation task
   * 
   * @param reportId Report ID to check status for
   * @returns The current status of the report generation
   */
  async getReportStatus(reportId: string): Promise<string> {
    try {
      const report = await this.getReport(reportId);
      return report.status;
    } catch (error) {
      console.error('Error getting report status:', error);
      return 'error';
    }
  }
  
  /**
   * Get all report findings for a project
   * 
   * @param projectId Project ID to get reports for
   * @returns An array of report findings or empty array if none exist
   */
  async getReportFindings(projectId: string): Promise<Finding[]> {
    try {
      // Look for findings with type='report' or name containing "report"
      const findings = await apiClient.get<Finding[]>(`/ctem/ui/findings?project_id=${projectId}`);
      if (findings && Array.isArray(findings)) {
        // Sort by created_at (newest first)
        const sortedFindings = [...findings].sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA; // Descending order (newest first)
        });
        
        // Filter findings that are reports
        const reportFindings = sortedFindings.filter(finding => 
          (finding.type === 'report' || (finding.name && finding.name.toLowerCase().includes('report')))
        );
        
        return reportFindings;
      }
      return [];
    } catch (error) {
      console.error('Error getting report findings:', error);
      return [];
    }
  }
  
  /**
   * Get the latest report finding for a project
   * 
   * @param projectId Project ID to get report for
   * @returns The latest report finding or null if none exists
   */
  async getLatestReportFinding(projectId: string): Promise<Finding | null> {
    const reports = await this.getReportFindings(projectId);
    return reports.length > 0 ? reports[0] : null;
  }
  
  /**
   * Get the content of a specific report by ID
   * 
   * @param reportId Report finding ID to get content for
   * @returns The report content as a string, or null if no report exists
   */
  async getReportContentById(reportId: string): Promise<string | null> {
    try {
      const report = await this.getReport(reportId);
      if (report && report.content) {
        return report.content;
      }
      return null;
    } catch (error) {
      console.error('Error getting report content:', error);
      return null;
    }
  }
  
  /**
   * Get the content of the latest report
   * 
   * @param projectId Project ID to get report content for
   * @returns The report content as a string, or null if no report exists
   */
  async getReportContent(projectId: string): Promise<string | null> {
    try {
      // First try new-style reports
      const latestReport = await this.getLatestReport(projectId);
      if (latestReport && latestReport.content) {
        return latestReport.content;
      }
      
      // Fall back to legacy finding-based reports
      const reportFinding = await this.getLatestReportFinding(projectId);
      
      if (!reportFinding || !reportFinding.has_file) {
        return null;
      }
      
      // Get the file content
      return this.getReportContentById(reportFinding.id);
    } catch (error) {
      console.error('Error getting report content:', error);
      return null;
    }
  }
}

// Export singleton instance
export const reportService = new ReportService();
export default reportService;